/**
 * Futures (COIN-M) REST API
 *
 * Access market data, manage accounts, and trade COIN-M perpetual and delivery futures.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    assertParamExists,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type {
    AccountInformationResponse,
    FuturesAccountBalanceResponse,
    GetCurrentPositionModeResponse,
    GetDownloadIdForFuturesOrderHistoryResponse,
    GetDownloadIdForFuturesTradeHistoryResponse,
    GetDownloadIdForFuturesTransactionHistoryResponse,
    GetFuturesOrderHistoryDownloadLinkByIdResponse,
    GetFuturesTradeDownloadLinkByIdResponse,
    GetFuturesTransactionHistoryDownloadLinkByIdResponse,
    GetIncomeHistoryResponse,
    NotionalBracketForPairResponse,
    NotionalBracketForSymbolResponse,
    UserCommissionRateResponse,
} from '../types';

/**
 * AccountApi - axios parameter creator
 */
const AccountApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Get current account information.
         *
         * Weight(IP): 5
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - for One-way Mode user, the "positions" will only show the "BOTH" positions
         * - for Hedge Mode user, the "positions" will show "BOTH", "LONG", and "SHORT" positions.
         *
         * @summary Account Information (USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        accountInformation: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/account',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Check futures account balance
         *
         * Weight(IP): 1
         *
         * Security Type: USER_DATA
         *
         * @summary Futures Account Balance (USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        futuresAccountBalance: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/balance',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get user's position mode (Hedge Mode or One-way Mode ) on ***EVERY symbol***
         *
         * Weight(IP): 30
         *
         * Security Type: USER_DATA
         *
         * @summary Get Current Position Mode (USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getCurrentPositionMode: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/positionSide/dual',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get Download Id For Futures Order History
         *
         * Weight(IP): 1000
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - Request Limitation is 8 times per month, shared by front end download page and rest api
         * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
         * - The time between `startTime` and `endTime` can not be longer than 1 year
         *
         * @summary Get Download Id For Futures Order History (USER_DATA)
         * @param {number | bigint} startTime Timestamp in ms
         * @param {number | bigint} endTime Timestamp in ms
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getDownloadIdForFuturesOrderHistory: async (
            startTime: number | bigint,
            endTime: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'startTime' is not null or undefined
            assertParamExists('getDownloadIdForFuturesOrderHistory', 'startTime', startTime);
            // verify required parameter 'endTime' is not null or undefined
            assertParamExists('getDownloadIdForFuturesOrderHistory', 'endTime', endTime);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/order/asyn',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get download id for futures trade history
         *
         * Weight(IP): 1000
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - Request Limitation is 8 times per month, shared by front end download page and rest api
         * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
         * - The time between `startTime` and `endTime` can not be longer than 1 year
         *
         * @summary Get Download Id For Futures Trade History (USER_DATA)
         * @param {number | bigint} startTime Timestamp in ms
         * @param {number | bigint} endTime Timestamp in ms
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getDownloadIdForFuturesTradeHistory: async (
            startTime: number | bigint,
            endTime: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'startTime' is not null or undefined
            assertParamExists('getDownloadIdForFuturesTradeHistory', 'startTime', startTime);
            // verify required parameter 'endTime' is not null or undefined
            assertParamExists('getDownloadIdForFuturesTradeHistory', 'endTime', endTime);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/trade/asyn',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get download id for futures transaction history
         *
         * Weight(IP): 1000
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - Request Limitation is 8 times per month, shared by front end download page and rest api
         * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
         * - The time between `startTime` and `endTime` can not be longer than 1 year
         *
         * @summary Get Download Id For Futures Transaction History (USER_DATA)
         * @param {number | bigint} startTime Timestamp in ms
         * @param {number | bigint} endTime Timestamp in ms
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getDownloadIdForFuturesTransactionHistory: async (
            startTime: number | bigint,
            endTime: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'startTime' is not null or undefined
            assertParamExists('getDownloadIdForFuturesTransactionHistory', 'startTime', startTime);
            // verify required parameter 'endTime' is not null or undefined
            assertParamExists('getDownloadIdForFuturesTransactionHistory', 'endTime', endTime);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/income/asyn',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get futures order history download link by Id
         *
         * Weight(IP): 5
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - Download link expiration: 7 days
         *
         * @summary Get Futures Order History Download Link by Id (USER_DATA)
         * @param {string} downloadId get by download id api
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getFuturesOrderHistoryDownloadLinkById: async (
            downloadId: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'downloadId' is not null or undefined
            assertParamExists('getFuturesOrderHistoryDownloadLinkById', 'downloadId', downloadId);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (downloadId !== undefined && downloadId !== null) {
                localVarQueryParameter['downloadId'] = downloadId;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/order/asyn/id',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get futures trade download link by Id
         *
         * Weight(IP): 5
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - Download link expiration: 7 days
         *
         * @summary Get Futures Trade Download Link by Id (USER_DATA)
         * @param {string} downloadId get by download id api
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getFuturesTradeDownloadLinkById: async (
            downloadId: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'downloadId' is not null or undefined
            assertParamExists('getFuturesTradeDownloadLinkById', 'downloadId', downloadId);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (downloadId !== undefined && downloadId !== null) {
                localVarQueryParameter['downloadId'] = downloadId;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/trade/asyn/id',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get futures transaction history download link by Id
         *
         * Weight(IP): 5
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - Download link expiration: 7 days
         *
         * @summary Get Futures Transaction History Download Link by Id (USER_DATA)
         * @param {string} downloadId get by download id api
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getFuturesTransactionHistoryDownloadLinkById: async (
            downloadId: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'downloadId' is not null or undefined
            assertParamExists(
                'getFuturesTransactionHistoryDownloadLinkById',
                'downloadId',
                downloadId
            );

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (downloadId !== undefined && downloadId !== null) {
                localVarQueryParameter['downloadId'] = downloadId;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/income/asyn/id',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get income history
         *
         * Weight(IP): 20
         *
         * Security Type: USER_DATA
         *
         * Notes:
         * - If `incomeType ` is not sent, all kinds of flow will be returned
         * - "trandId" is unique in the same "incomeType" for a user
         * - The time between `startTime` and `endTime` can not be longer than 1 year
         *
         * @summary Get Income History (USER_DATA)
         * @param {string} [symbol] Symbol
         * @param {GetIncomeHistoryIncomeTypeEnum} [incomeType] Income type.
         * @param {number | bigint} [startTime] Timestamp in ms to get funding from INCLUSIVE.
         * @param {number | bigint} [endTime] Timestamp in ms to get funding until INCLUSIVE.
         * @param {number | bigint} [page] Page number
         * @param {number | bigint} [limit] Maximum number of records to return.
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getIncomeHistory: async (
            symbol?: string,
            incomeType?: GetIncomeHistoryIncomeTypeEnum,
            startTime?: number | bigint,
            endTime?: number | bigint,
            page?: number | bigint,
            limit?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (incomeType !== undefined && incomeType !== null) {
                localVarQueryParameter['incomeType'] = incomeType;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (page !== undefined && page !== null) {
                localVarQueryParameter['page'] = page;
            }
            if (limit !== undefined && limit !== null) {
                localVarQueryParameter['limit'] = limit;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/income',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * **Not recommended to continue using this v1 endpoint**
         *
         * Get the pair's default notional bracket list, may return ambiguous
         * values when there have been multiple different `symbol` brackets under
         * the `pair`, suggest using the following `GET /dapi/v2/leverageBracket`
         * query instead to get the specific `symbol` notional bracket list.
         *
         * Weight(IP): 1
         *
         * Security Type: USER_DATA
         *
         * @summary Notional Bracket for Pair (USER_DATA)
         * @param {string} [pair]
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        notionalBracketForPair: async (
            pair?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (pair !== undefined && pair !== null) {
                localVarQueryParameter['pair'] = pair;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/leverageBracket',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get the symbol's notional bracket list.
         *
         * Weight: 1 (after CM migration: 1 with `symbol` / 2 without `symbol`)
         *
         * Security Type: USER_DATA
         *
         * @summary Notional Bracket for Symbol (USER_DATA)
         * @param {string} [symbol]
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        notionalBracketForSymbol: async (
            symbol?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v2/leverageBracket',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query user commission rate
         *
         * Weight(IP): 20
         *
         * Security Type: USER_DATA
         *
         * @summary User Commission Rate (USER_DATA)
         * @param {string} symbol Symbol
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        userCommissionRate: async (
            symbol: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('userCommissionRate', 'symbol', symbol);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/dapi/v1/commissionRate',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * AccountApi - interface
 * @interface AccountApi
 */
export interface AccountApiInterface {
    /**
     * Get current account information.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - for One-way Mode user, the "positions" will only show the "BOTH" positions
     * - for Hedge Mode user, the "positions" will show "BOTH", "LONG", and "SHORT" positions.
     *
     * @summary Account Information (USER_DATA)
     * @param {AccountInformationRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    accountInformation(
        requestParameters?: AccountInformationRequest
    ): Promise<RestApiResponse<AccountInformationResponse>>;
    /**
     * Check futures account balance
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * @summary Futures Account Balance (USER_DATA)
     * @param {FuturesAccountBalanceRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    futuresAccountBalance(
        requestParameters?: FuturesAccountBalanceRequest
    ): Promise<RestApiResponse<FuturesAccountBalanceResponse>>;
    /**
     * Get user's position mode (Hedge Mode or One-way Mode ) on ***EVERY symbol***
     *
     * Weight(IP): 30
     *
     * Security Type: USER_DATA
     *
     * @summary Get Current Position Mode (USER_DATA)
     * @param {GetCurrentPositionModeRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getCurrentPositionMode(
        requestParameters?: GetCurrentPositionModeRequest
    ): Promise<RestApiResponse<GetCurrentPositionModeResponse>>;
    /**
     * Get Download Id For Futures Order History
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Order History (USER_DATA)
     * @param {GetDownloadIdForFuturesOrderHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getDownloadIdForFuturesOrderHistory(
        requestParameters: GetDownloadIdForFuturesOrderHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesOrderHistoryResponse>>;
    /**
     * Get download id for futures trade history
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Trade History (USER_DATA)
     * @param {GetDownloadIdForFuturesTradeHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getDownloadIdForFuturesTradeHistory(
        requestParameters: GetDownloadIdForFuturesTradeHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesTradeHistoryResponse>>;
    /**
     * Get download id for futures transaction history
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Transaction History (USER_DATA)
     * @param {GetDownloadIdForFuturesTransactionHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getDownloadIdForFuturesTransactionHistory(
        requestParameters: GetDownloadIdForFuturesTransactionHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesTransactionHistoryResponse>>;
    /**
     * Get futures order history download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Order History Download Link by Id (USER_DATA)
     * @param {GetFuturesOrderHistoryDownloadLinkByIdRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getFuturesOrderHistoryDownloadLinkById(
        requestParameters: GetFuturesOrderHistoryDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesOrderHistoryDownloadLinkByIdResponse>>;
    /**
     * Get futures trade download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Trade Download Link by Id (USER_DATA)
     * @param {GetFuturesTradeDownloadLinkByIdRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getFuturesTradeDownloadLinkById(
        requestParameters: GetFuturesTradeDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesTradeDownloadLinkByIdResponse>>;
    /**
     * Get futures transaction history download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Transaction History Download Link by Id (USER_DATA)
     * @param {GetFuturesTransactionHistoryDownloadLinkByIdRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getFuturesTransactionHistoryDownloadLinkById(
        requestParameters: GetFuturesTransactionHistoryDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesTransactionHistoryDownloadLinkByIdResponse>>;
    /**
     * Get income history
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - If `incomeType ` is not sent, all kinds of flow will be returned
     * - "trandId" is unique in the same "incomeType" for a user
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Income History (USER_DATA)
     * @param {GetIncomeHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getIncomeHistory(
        requestParameters?: GetIncomeHistoryRequest
    ): Promise<RestApiResponse<GetIncomeHistoryResponse>>;
    /**
     * **Not recommended to continue using this v1 endpoint**
     *
     * Get the pair's default notional bracket list, may return ambiguous
     * values when there have been multiple different `symbol` brackets under
     * the `pair`, suggest using the following `GET /dapi/v2/leverageBracket`
     * query instead to get the specific `symbol` notional bracket list.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * @summary Notional Bracket for Pair (USER_DATA)
     * @param {NotionalBracketForPairRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    notionalBracketForPair(
        requestParameters?: NotionalBracketForPairRequest
    ): Promise<RestApiResponse<NotionalBracketForPairResponse>>;
    /**
     * Get the symbol's notional bracket list.
     *
     * Weight: 1 (after CM migration: 1 with `symbol` / 2 without `symbol`)
     *
     * Security Type: USER_DATA
     *
     * @summary Notional Bracket for Symbol (USER_DATA)
     * @param {NotionalBracketForSymbolRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    notionalBracketForSymbol(
        requestParameters?: NotionalBracketForSymbolRequest
    ): Promise<RestApiResponse<NotionalBracketForSymbolResponse>>;
    /**
     * Query user commission rate
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * @summary User Commission Rate (USER_DATA)
     * @param {UserCommissionRateRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    userCommissionRate(
        requestParameters: UserCommissionRateRequest
    ): Promise<RestApiResponse<UserCommissionRateResponse>>;
}

/**
 * Request parameters for accountInformation operation in AccountApi.
 * @interface AccountInformationRequest
 */
export interface AccountInformationRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiAccountInformation
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for futuresAccountBalance operation in AccountApi.
 * @interface FuturesAccountBalanceRequest
 */
export interface FuturesAccountBalanceRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiFuturesAccountBalance
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getCurrentPositionMode operation in AccountApi.
 * @interface GetCurrentPositionModeRequest
 */
export interface GetCurrentPositionModeRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetCurrentPositionMode
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getDownloadIdForFuturesOrderHistory operation in AccountApi.
 * @interface GetDownloadIdForFuturesOrderHistoryRequest
 */
export interface GetDownloadIdForFuturesOrderHistoryRequest {
    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesOrderHistory
     */
    readonly startTime: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesOrderHistory
     */
    readonly endTime: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesOrderHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getDownloadIdForFuturesTradeHistory operation in AccountApi.
 * @interface GetDownloadIdForFuturesTradeHistoryRequest
 */
export interface GetDownloadIdForFuturesTradeHistoryRequest {
    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesTradeHistory
     */
    readonly startTime: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesTradeHistory
     */
    readonly endTime: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesTradeHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getDownloadIdForFuturesTransactionHistory operation in AccountApi.
 * @interface GetDownloadIdForFuturesTransactionHistoryRequest
 */
export interface GetDownloadIdForFuturesTransactionHistoryRequest {
    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesTransactionHistory
     */
    readonly startTime: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesTransactionHistory
     */
    readonly endTime: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetDownloadIdForFuturesTransactionHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getFuturesOrderHistoryDownloadLinkById operation in AccountApi.
 * @interface GetFuturesOrderHistoryDownloadLinkByIdRequest
 */
export interface GetFuturesOrderHistoryDownloadLinkByIdRequest {
    /**
     * get by download id api
     * @type {string}
     * @memberof AccountApiGetFuturesOrderHistoryDownloadLinkById
     */
    readonly downloadId: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetFuturesOrderHistoryDownloadLinkById
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getFuturesTradeDownloadLinkById operation in AccountApi.
 * @interface GetFuturesTradeDownloadLinkByIdRequest
 */
export interface GetFuturesTradeDownloadLinkByIdRequest {
    /**
     * get by download id api
     * @type {string}
     * @memberof AccountApiGetFuturesTradeDownloadLinkById
     */
    readonly downloadId: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetFuturesTradeDownloadLinkById
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getFuturesTransactionHistoryDownloadLinkById operation in AccountApi.
 * @interface GetFuturesTransactionHistoryDownloadLinkByIdRequest
 */
export interface GetFuturesTransactionHistoryDownloadLinkByIdRequest {
    /**
     * get by download id api
     * @type {string}
     * @memberof AccountApiGetFuturesTransactionHistoryDownloadLinkById
     */
    readonly downloadId: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetFuturesTransactionHistoryDownloadLinkById
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getIncomeHistory operation in AccountApi.
 * @interface GetIncomeHistoryRequest
 */
export interface GetIncomeHistoryRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof AccountApiGetIncomeHistory
     */
    readonly symbol?: string;

    /**
     * Income type.
     * @type {'TRANSFER' | 'WELCOME_BONUS' | 'FUNDING_FEE' | 'REALIZED_PNL' | 'COMMISSION' | 'INSURANCE_CLEAR' | 'DELIVERED_SETTELMENT'}
     * @memberof AccountApiGetIncomeHistory
     */
    readonly incomeType?: GetIncomeHistoryIncomeTypeEnum;

    /**
     * Timestamp in ms to get funding from INCLUSIVE.
     * @type {number | bigint}
     * @memberof AccountApiGetIncomeHistory
     */
    readonly startTime?: number | bigint;

    /**
     * Timestamp in ms to get funding until INCLUSIVE.
     * @type {number | bigint}
     * @memberof AccountApiGetIncomeHistory
     */
    readonly endTime?: number | bigint;

    /**
     * Page number
     * @type {number | bigint}
     * @memberof AccountApiGetIncomeHistory
     */
    readonly page?: number | bigint;

    /**
     * Maximum number of records to return.
     * @type {number | bigint}
     * @memberof AccountApiGetIncomeHistory
     */
    readonly limit?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetIncomeHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for notionalBracketForPair operation in AccountApi.
 * @interface NotionalBracketForPairRequest
 */
export interface NotionalBracketForPairRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiNotionalBracketForPair
     */
    readonly pair?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiNotionalBracketForPair
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for notionalBracketForSymbol operation in AccountApi.
 * @interface NotionalBracketForSymbolRequest
 */
export interface NotionalBracketForSymbolRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiNotionalBracketForSymbol
     */
    readonly symbol?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiNotionalBracketForSymbol
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for userCommissionRate operation in AccountApi.
 * @interface UserCommissionRateRequest
 */
export interface UserCommissionRateRequest {
    /**
     * Symbol
     * @type {string}
     * @memberof AccountApiUserCommissionRate
     */
    readonly symbol: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiUserCommissionRate
     */
    readonly recvWindow?: number | bigint;
}

/**
 * AccountApi - object-oriented interface
 * @class AccountApi
 */
export class AccountApi implements AccountApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = AccountApiAxiosParamCreator(configuration);
    }

    /**
     * Get current account information.
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - for One-way Mode user, the "positions" will only show the "BOTH" positions
     * - for Hedge Mode user, the "positions" will show "BOTH", "LONG", and "SHORT" positions.
     *
     * @summary Account Information (USER_DATA)
     * @param {AccountInformationRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<AccountInformationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#account-information Binance API Documentation}
     */
    public async accountInformation(
        requestParameters: AccountInformationRequest = {}
    ): Promise<RestApiResponse<AccountInformationResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.accountInformation(
            requestParameters?.recvWindow
        );
        return sendRequest<AccountInformationResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Check futures account balance
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * @summary Futures Account Balance (USER_DATA)
     * @param {FuturesAccountBalanceRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<FuturesAccountBalanceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#futures-account-balance Binance API Documentation}
     */
    public async futuresAccountBalance(
        requestParameters: FuturesAccountBalanceRequest = {}
    ): Promise<RestApiResponse<FuturesAccountBalanceResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.futuresAccountBalance(
            requestParameters?.recvWindow
        );
        return sendRequest<FuturesAccountBalanceResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get user's position mode (Hedge Mode or One-way Mode ) on ***EVERY symbol***
     *
     * Weight(IP): 30
     *
     * Security Type: USER_DATA
     *
     * @summary Get Current Position Mode (USER_DATA)
     * @param {GetCurrentPositionModeRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetCurrentPositionModeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-current-position-mode Binance API Documentation}
     */
    public async getCurrentPositionMode(
        requestParameters: GetCurrentPositionModeRequest = {}
    ): Promise<RestApiResponse<GetCurrentPositionModeResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getCurrentPositionMode(
            requestParameters?.recvWindow
        );
        return sendRequest<GetCurrentPositionModeResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get Download Id For Futures Order History
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Order History (USER_DATA)
     * @param {GetDownloadIdForFuturesOrderHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetDownloadIdForFuturesOrderHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-download-id-for-futures-order-history Binance API Documentation}
     */
    public async getDownloadIdForFuturesOrderHistory(
        requestParameters: GetDownloadIdForFuturesOrderHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesOrderHistoryResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getDownloadIdForFuturesOrderHistory(
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.recvWindow
            );
        return sendRequest<GetDownloadIdForFuturesOrderHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get download id for futures trade history
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Trade History (USER_DATA)
     * @param {GetDownloadIdForFuturesTradeHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetDownloadIdForFuturesTradeHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-download-id-for-futures-trade-history Binance API Documentation}
     */
    public async getDownloadIdForFuturesTradeHistory(
        requestParameters: GetDownloadIdForFuturesTradeHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesTradeHistoryResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getDownloadIdForFuturesTradeHistory(
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.recvWindow
            );
        return sendRequest<GetDownloadIdForFuturesTradeHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get download id for futures transaction history
     *
     * Weight(IP): 1000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Request Limitation is 8 times per month, shared by front end download page and rest api
     * - This endpoint uses the IP rate limit bucket and costs 1000 weight per call. The maximum is 2 calls per minute; the 3rd call within the same minute will trigger a ban.
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Download Id For Futures Transaction History (USER_DATA)
     * @param {GetDownloadIdForFuturesTransactionHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetDownloadIdForFuturesTransactionHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-download-id-for-futures-transaction-history Binance API Documentation}
     */
    public async getDownloadIdForFuturesTransactionHistory(
        requestParameters: GetDownloadIdForFuturesTransactionHistoryRequest
    ): Promise<RestApiResponse<GetDownloadIdForFuturesTransactionHistoryResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getDownloadIdForFuturesTransactionHistory(
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.recvWindow
            );
        return sendRequest<GetDownloadIdForFuturesTransactionHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get futures order history download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Order History Download Link by Id (USER_DATA)
     * @param {GetFuturesOrderHistoryDownloadLinkByIdRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetFuturesOrderHistoryDownloadLinkByIdResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-futures-order-history-download-link-by-id Binance API Documentation}
     */
    public async getFuturesOrderHistoryDownloadLinkById(
        requestParameters: GetFuturesOrderHistoryDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesOrderHistoryDownloadLinkByIdResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getFuturesOrderHistoryDownloadLinkById(
                requestParameters?.downloadId,
                requestParameters?.recvWindow
            );
        return sendRequest<GetFuturesOrderHistoryDownloadLinkByIdResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get futures trade download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Trade Download Link by Id (USER_DATA)
     * @param {GetFuturesTradeDownloadLinkByIdRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetFuturesTradeDownloadLinkByIdResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-futures-trade-download-link-by-id Binance API Documentation}
     */
    public async getFuturesTradeDownloadLinkById(
        requestParameters: GetFuturesTradeDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesTradeDownloadLinkByIdResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getFuturesTradeDownloadLinkById(
                requestParameters?.downloadId,
                requestParameters?.recvWindow
            );
        return sendRequest<GetFuturesTradeDownloadLinkByIdResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get futures transaction history download link by Id
     *
     * Weight(IP): 5
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - Download link expiration: 7 days
     *
     * @summary Get Futures Transaction History Download Link by Id (USER_DATA)
     * @param {GetFuturesTransactionHistoryDownloadLinkByIdRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetFuturesTransactionHistoryDownloadLinkByIdResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-futures-transaction-history-download-link-by-id Binance API Documentation}
     */
    public async getFuturesTransactionHistoryDownloadLinkById(
        requestParameters: GetFuturesTransactionHistoryDownloadLinkByIdRequest
    ): Promise<RestApiResponse<GetFuturesTransactionHistoryDownloadLinkByIdResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getFuturesTransactionHistoryDownloadLinkById(
                requestParameters?.downloadId,
                requestParameters?.recvWindow
            );
        return sendRequest<GetFuturesTransactionHistoryDownloadLinkByIdResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get income history
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - If `incomeType ` is not sent, all kinds of flow will be returned
     * - "trandId" is unique in the same "incomeType" for a user
     * - The time between `startTime` and `endTime` can not be longer than 1 year
     *
     * @summary Get Income History (USER_DATA)
     * @param {GetIncomeHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetIncomeHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#get-income-history Binance API Documentation}
     */
    public async getIncomeHistory(
        requestParameters: GetIncomeHistoryRequest = {}
    ): Promise<RestApiResponse<GetIncomeHistoryResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getIncomeHistory(
            requestParameters?.symbol,
            requestParameters?.incomeType,
            requestParameters?.startTime,
            requestParameters?.endTime,
            requestParameters?.page,
            requestParameters?.limit,
            requestParameters?.recvWindow
        );
        return sendRequest<GetIncomeHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * **Not recommended to continue using this v1 endpoint**
     *
     * Get the pair's default notional bracket list, may return ambiguous
     * values when there have been multiple different `symbol` brackets under
     * the `pair`, suggest using the following `GET /dapi/v2/leverageBracket`
     * query instead to get the specific `symbol` notional bracket list.
     *
     * Weight(IP): 1
     *
     * Security Type: USER_DATA
     *
     * @summary Notional Bracket for Pair (USER_DATA)
     * @param {NotionalBracketForPairRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<NotionalBracketForPairResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#notional-bracket-for-pair Binance API Documentation}
     */
    public async notionalBracketForPair(
        requestParameters: NotionalBracketForPairRequest = {}
    ): Promise<RestApiResponse<NotionalBracketForPairResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.notionalBracketForPair(
            requestParameters?.pair,
            requestParameters?.recvWindow
        );
        return sendRequest<NotionalBracketForPairResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get the symbol's notional bracket list.
     *
     * Weight: 1 (after CM migration: 1 with `symbol` / 2 without `symbol`)
     *
     * Security Type: USER_DATA
     *
     * @summary Notional Bracket for Symbol (USER_DATA)
     * @param {NotionalBracketForSymbolRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<NotionalBracketForSymbolResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#notional-bracket-for-symbol Binance API Documentation}
     */
    public async notionalBracketForSymbol(
        requestParameters: NotionalBracketForSymbolRequest = {}
    ): Promise<RestApiResponse<NotionalBracketForSymbolResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.notionalBracketForSymbol(
            requestParameters?.symbol,
            requestParameters?.recvWindow
        );
        return sendRequest<NotionalBracketForSymbolResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query user commission rate
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * @summary User Commission Rate (USER_DATA)
     * @param {UserCommissionRateRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<UserCommissionRateResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-coin-m-futures/api/rest-api/account#user-commission-rate Binance API Documentation}
     */
    public async userCommissionRate(
        requestParameters: UserCommissionRateRequest
    ): Promise<RestApiResponse<UserCommissionRateResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.userCommissionRate(
            requestParameters?.symbol,
            requestParameters?.recvWindow
        );
        return sendRequest<UserCommissionRateResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }
}

export enum GetIncomeHistoryIncomeTypeEnum {
    TRANSFER = 'TRANSFER',
    WELCOME_BONUS = 'WELCOME_BONUS',
    FUNDING_FEE = 'FUNDING_FEE',
    REALIZED_PNL = 'REALIZED_PNL',
    COMMISSION = 'COMMISSION',
    INSURANCE_CLEAR = 'INSURANCE_CLEAR',
    DELIVERED_SETTELMENT = 'DELIVERED_SETTELMENT',
}
